import React, { useState, useEffect } from 'react';
import { buildFileTree } from '../utils/fileTree';
import { API_BASE_URL } from '../config/constants';

function FileBrowserItem({ item, level = 0, onFileClick, onFolderClick }) {
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels
  const [lineCount, setLineCount] = useState(null);

  const isFolder = item.type === 'folder';
  const hasChildren = isFolder && item.children && item.children.length > 0;

  const handleClick = () => {
    if (isFolder) {
      if (hasChildren) {
        setIsExpanded(!isExpanded);
      }
      // Call onFolderClick to change scope
      if (onFolderClick) {
        onFolderClick(item);
      }
    } else if (!isFolder && onFileClick) {
      onFileClick(item);
    }
  };

  const indent = level * 16;

  // Fetch line count for files
  useEffect(() => {
    if (isFolder || !item.path) {
      return;
    }

    const fetchLineCount = async () => {
      try {
        // Remove root folder from path - server expects relative path without root
        const pathParts = item.path.split('/');
        const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : item.path;
        
        const encodedPath = encodeURIComponent(relativePath);
        const response = await fetch(`${API_BASE_URL}/read?path=${encodedPath}`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          return;
        }

        // Try to parse as JSON first, fall back to plain text
        const contentType = response.headers.get('content-type');
        let fileContent = '';
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          fileContent = data.content || data || '';
        } else {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            fileContent = data.content || data || '';
          } catch (e) {
            fileContent = text;
          }
        }
        
        // Calculate line count from the actual content
        const count = fileContent.length === 0 ? 0 : fileContent.split('\n').length;
        setLineCount(count);
      } catch (err) {
        // Silently fail - line count is optional
        console.error('Error fetching line count:', err);
      }
    };

    fetchLineCount();
  }, [isFolder, item.path]);

  return (
    <div>
      <div
        onClick={handleClick}
        style={{
          padding: '6px 12px',
          paddingLeft: `${12 + indent}px`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          fontSize: '13px',
          color: '#1e1e1e',
          userSelect: 'none',
          transition: 'background-color 0.15s',
          borderRadius: '4px',
          margin: '1px 8px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {isFolder ? (
          <span style={{ marginRight: '6px', fontSize: '10px', width: '14px', display: 'inline-block', color: '#6b7280' }}>
            {hasChildren ? (isExpanded ? '▼' : '▶') : '📁'}
          </span>
        ) : (
          <span style={{ marginRight: '6px', fontSize: '10px', width: '14px', display: 'inline-block' }}>📄</span>
        )}
        <span style={{ flex: 1 }}>{item.name}</span>
        {!isFolder && lineCount !== null && (
          <span style={{
            fontSize: '11px',
            color: '#6b7280',
            background: '#f3f4f6',
            padding: '2px 8px',
            borderRadius: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            marginLeft: '8px',
            flexShrink: 0,
            fontWeight: '400'
          }}>
            {lineCount}
          </span>
        )}
      </div>
      {isFolder && hasChildren && isExpanded && (
        <div>
          {item.children.map((child, index) => (
            <FileBrowserItem
              key={`${child.path}-${index}`}
              item={child}
              level={level + 1}
              onFileClick={onFileClick}
              onFolderClick={onFolderClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileBrowser({ files, onFileClick, onFolderClick }) {
  if (!files || files.length === 0) {
    return (
      <div style={{ padding: '20px', color: '#666', fontSize: '14px' }}>
        No files found
      </div>
    );
  }

  // Check if files is already a tree structure (array of objects with 'type' property)
  // or raw file paths (array of strings)
  const isAlreadyTree = files.length > 0 && typeof files[0] === 'object' && files[0].type;
  const tree = isAlreadyTree ? files : buildFileTree(files);

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      fontFamily: 'monospace',
    }}>
      {tree.map((item, index) => (
        <FileBrowserItem
          key={`${item.path}-${index}`}
          item={item}
          level={0}
          onFileClick={onFileClick}
          onFolderClick={onFolderClick}
        />
      ))}
    </div>
  );
}

export default FileBrowser;

